import type { TSearchDocument, TSearchMatch, TSearchMatchSpan, TSearchOptions } from '../types/TSearch';
import { isSearchWordCharacterAt } from './isSearchWordCharacterAt';

const toLengthPreservingLowerCase = (value: string) => {
  let result = '';

  for (const character of value) {
    const lowered = character.toLowerCase();
    result += lowered.length === character.length ? lowered : character;
  }

  return result;
};

export const findSearchMatches = (
  document: TSearchDocument,
  query: string,
  options: TSearchOptions,
): TSearchMatch[] => {
  if (!query.trim() || !document.text) {
    return [];
  }

  const normalizedQuery = query.replace(/\s+/g, ' ');
  const searchText = options.caseSensitive ? document.text : toLengthPreservingLowerCase(document.text);
  const searchQuery = options.caseSensitive ? normalizedQuery : toLengthPreservingLowerCase(normalizedQuery);
  const matches: TSearchMatch[] = [];
  let searchIndex = 0;
  let rangeCursor = 0;

  while (searchIndex <= searchText.length - searchQuery.length) {
    const matchStart = searchText.indexOf(searchQuery, searchIndex);

    if (matchStart < 0) {
      break;
    }

    const matchEnd = matchStart + searchQuery.length;

    if (
      options.wholeWord &&
      (isSearchWordCharacterAt(document.text, matchStart - 1) || isSearchWordCharacterAt(document.text, matchEnd))
    ) {
      searchIndex = matchStart + 1;
      continue;
    }

    searchIndex = matchEnd;

    while (rangeCursor < document.wordRanges.length && document.wordRanges[rangeCursor].end <= matchStart) {
      rangeCursor += 1;
    }

    const spans: TSearchMatchSpan[] = [];
    let spanCursor = rangeCursor;

    while (spanCursor < document.wordRanges.length && document.wordRanges[spanCursor].start < matchEnd) {
      const wordRange = document.wordRanges[spanCursor];
      const spanStart = Math.max(matchStart, wordRange.start);
      const spanEnd = Math.min(matchEnd, wordRange.end);

      if (spanStart < spanEnd) {
        const normalizedStart = spanStart - wordRange.start;
        const normalizedEnd = spanEnd - wordRange.start;

        spans.push({
          key: wordRange.key,
          itemIndex: wordRange.itemIndex,
          startChar: wordRange.offsetMap ? wordRange.offsetMap[normalizedStart] : normalizedStart,
          endChar: wordRange.offsetMap ? wordRange.offsetMap[normalizedEnd - 1] + 1 : normalizedEnd,
        });
      }

      spanCursor += 1;
    }

    const firstSpan = spans[0];

    if (firstSpan) {
      matches.push({
        itemIndex: firstSpan.itemIndex,
        spans,
      });
    }
  }

  return matches;
};
